import Phaser from 'phaser';
import { REACTION_COMBO_MS, findReaction, reactionUrl } from '@campus/core';

/** Où afficher une réaction : la tête de l'avatar, suivie à chaque image. */
export interface ReactionAnchor { x: number; y: number }

const SIZE = 36;            // px du monde (image source 72 px : nette en zoom 200 %)
const HEAD_OFFSET = 80;     // au-dessus de l'ancre (pieds) de l'avatar
const FLOAT_PX = 64;
const FLOAT_MS = 1_400;
const BADGE_HIDE_MS = 1_200;

interface Combo {
  id: string;
  count: number;
  lastAt: number;
  badge: Phaser.GameObjects.Text | null;
  hideAt: number;
}

/**
 * Réactions emoji au-dessus des avatars (images Fluent 3D, @campus/core/reactions).
 *
 * Chaque réaction s'envole de la tête de l'avatar. Enchaînement : la même
 * réaction renvoyée moins de REACTION_COMBO_MS après la précédente fait monter
 * un compteur « ×N » à côté de la tête, qui grossit et se colore avec la série,
 * et les emojis suivants partent un peu plus gros. Images chargées à la
 * demande (texture `reaction:{id}`, lissée : voir pixelFilter.ts).
 */
export class ReactionEffects {
  private combos = new Map<string, Combo>();
  private pending = new Map<string, Array<() => void>>();
  private readonly onUpdate = () => this.updateBadges();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
    private readonly anchorOf: (userId: string) => ReactionAnchor | null,
  ) {
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
  }

  destroy(): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    for (const c of this.combos.values()) c.badge?.destroy();
    this.combos.clear();
    this.pending.clear();
  }

  show(userId: string, id: string): void {
    if (!findReaction(id) || !this.anchorOf(userId)) return;
    const now = this.scene.time.now;
    let combo = this.combos.get(userId);
    if (combo && combo.id === id && now - combo.lastAt < REACTION_COMBO_MS) {
      combo.count += 1;
    } else {
      combo?.badge?.destroy();
      combo = { id, count: 1, lastAt: now, badge: null, hideAt: 0 };
      this.combos.set(userId, combo);
    }
    combo.lastAt = now;
    const count = combo.count;
    this.withTexture(id, () => this.spawn(userId, id, count));
    if (count >= 2) this.bumpBadge(userId, combo);
  }

  private withTexture(id: string, then: () => void): void {
    const key = `reaction:${id}`;
    if (this.scene.textures.exists(key)) { then(); return; }
    const waiting = this.pending.get(key);
    if (waiting) { waiting.push(then); return; }
    this.pending.set(key, [then]);
    this.scene.load.image(key, reactionUrl(id));
    this.scene.load.once(`filecomplete-image-${key}`, () => {
      const list = this.pending.get(key) ?? [];
      this.pending.delete(key);
      for (const fn of list) fn();
    });
    this.scene.load.start();
  }

  private spawn(userId: string, id: string, count: number): void {
    const anchor = this.anchorOf(userId);
    if (!anchor) return;
    // Une série fait partir des emojis un peu plus gros, et en éventail.
    const scale = Math.min(1 + (count - 1) * 0.06, 1.6);
    const drift = count > 1 ? Phaser.Math.Between(-18, 18) : 0;
    const img = this.scene.add.image(anchor.x, anchor.y - HEAD_OFFSET, `reaction:${id}`);
    img.setDisplaySize(SIZE, SIZE);
    const base = img.scale * scale;
    img.setOrigin(0.5, 1).setDepth(this.depth + 0.1).setScale(0).setAngle(Phaser.Math.Between(-8, 8));
    this.scene.tweens.add({ targets: img, scale: base * 1.2, duration: 140, ease: 'Back.easeOut',
      onComplete: () => this.scene.tweens.add({ targets: img, scale: base, duration: 120, ease: 'Sine.easeInOut' }) });
    this.scene.tweens.add({ targets: img, y: img.y - FLOAT_PX, x: img.x + drift, duration: FLOAT_MS, ease: 'Sine.easeOut' });
    this.scene.tweens.add({ targets: img, alpha: 0, delay: FLOAT_MS - 450, duration: 450, onComplete: () => img.destroy() });
  }

  private bumpBadge(userId: string, combo: Combo): void {
    const anchor = this.anchorOf(userId);
    if (!anchor) return;
    if (!combo.badge) {
      combo.badge = this.scene.add.text(0, 0, '', {
        fontFamily: 'Inter, sans-serif', fontStyle: '800', fontSize: '18px',
        color: '#ffffff', stroke: '#1d1b2e', strokeThickness: 5, resolution: 2,
      }).setOrigin(0, 1).setDepth(this.depth + 0.2);
    }
    const badge = combo.badge;
    badge.setText(`×${combo.count}`);
    badge.setColor(combo.count >= 10 ? '#ff5a5a' : combo.count >= 5 ? '#ffb340' : '#ffffff');
    combo.hideAt = this.scene.time.now + BADGE_HIDE_MS;
    this.placeBadge(badge, anchor);
    badge.setAlpha(1);
    const peak = Math.min(1.3 + combo.count * 0.03, 1.8);
    this.scene.tweens.killTweensOf(badge);
    badge.setScale(peak);
    this.scene.tweens.add({ targets: badge, scale: Math.min(1 + combo.count * 0.02, 1.4), duration: 220, ease: 'Back.easeOut' });
  }

  private placeBadge(badge: Phaser.GameObjects.Text, anchor: ReactionAnchor): void {
    badge.setPosition(anchor.x + SIZE / 2 + 2, anchor.y - HEAD_OFFSET - SIZE / 2);
  }

  /** Compteurs : suivent l'avatar, disparaissent un peu après la dernière réaction. */
  private updateBadges(): void {
    const now = this.scene.time.now;
    for (const [userId, combo] of this.combos) {
      const anchor = this.anchorOf(userId);
      if (!anchor || now - combo.lastAt > REACTION_COMBO_MS + BADGE_HIDE_MS) {
        combo.badge?.destroy();
        this.combos.delete(userId);
        continue;
      }
      if (!combo.badge) continue;
      this.placeBadge(combo.badge, anchor);
      if (now > combo.hideAt && combo.badge.alpha === 1) {
        this.scene.tweens.add({ targets: combo.badge, alpha: 0, duration: 250 });
      }
    }
  }
}
